/**
 * A query-builder component defines a step like query, consisting of
 * selection of events and exclusion steps
 */

import findKey from 'lodash/findKey';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import debounce from 'lodash/debounce';
import {AttrsSchema, Component} from 'panel';

import {dataGroupIDsEqual, serializeAttributes} from 'common/util';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {findKeyByValue} from 'common/util/typescript';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {ResourceType} from 'common/report/constants';
import {QueryEntrySection} from 'common/widgets/query-entry-section';
import {
  EntrySectionUpdateEvent,
  FiltersOperator,
  isEventBasedType,
  isGroupedEntry,
  isUserBasedType,
  QueryEntry,
  QueryFilterEntry,
  QuerySegmentEntry,
  RowLabelType,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {customEventsClient} from 'common/report/queries/app-api/custom-events-client';
import {isBoardsUiEnabled} from 'common/report/util/boards';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {maxQueryReason} from 'common/report/util/query-tracking';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {DataGroup, SelectWarning} from 'common/widgets/data-group-selector/types';
import {dataGroupsClient} from 'common/report/queries/app-api/data-groups-client';
import {DEFAULT_DATA_GROUP} from 'common/widgets/data-group-selector';
import {isEventSegment, isMultiGroupBy, Segment} from 'common/widgets/query-block/segment-menu/types';
import {Store, waitForStoreValue} from 'common/report/stores/store';
import {topEventsStore} from 'common/report/stores/events';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {eventPropertiesStore} from 'common/report/stores/event-properties';
import {peoplePropertiesStore} from 'common/report/stores/people-properties';
import {eventPropertyDefinitionsStore, peoplePropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {SPECIAL_EVENTS} from 'common/report/constants';
import {currentProjectStore} from 'common/report/stores/project-details';
import {stickyFixed} from 'common/util/dom-sticky';
import {chromeHeaderHeight, chromeHeaderPrimaryHeight} from 'common/widgets/chrome/chrome-header/constants';

import {CAPTURED_TRACKING, TrackingEvent} from './tracking';
import {Filter, QueryBuilderAttrs, QueryBuilderSelection} from './types';
import {queryIsInvalidForDataGroup, setQueryDataGroup} from './util/data-groups';
import {
  qbStyleAttrs,
  sanitizeExclusionSteps,
  sanitizeGroupBys,
  transformFromQuerySegmentEntries,
  transformToQuerySegmentEntries,
} from './util';
import {template} from './template';
import css from './index.styl?inline';

type EventSourceName = `entries` | `exclusion-steps`;

const COMPONENT_NAME = `mp-query-builder`;

/* eslint-disable @typescript-eslint/naming-convention */
enum AttributeFields {
  attributionSelection = `breakdown-attribution-selection`,
  dataGroupId = `data-group-id`,
  dateRange = `date-range`,
  entries = `entries`,
  exclusionSteps = `exclusion-steps`,
  filters = `filters`,
  filtersOperator = `filters-operator`,
  groupBy = `group-by`,
}
/* eslint-enable */

const EVENT_SOURCE_MAP = Object.freeze({
  entries: `entries`,
  'exclusion-steps': `exclusionSteps`,
});

const ATTRIBUTE_READABLE_MAP = Object.freeze({
  entries: `event`,
  'exclusion-steps': `exclusion step`,
  'group-by': `segment`,
});

interface QueryBuilderState {
  customEvents: CustomEventDefinition[];
  dataGroups: DataGroup[];
  selectionObject: QueryBuilderSelection;
}

export class QueryBuilder extends Component<QueryBuilderState, QueryBuilderAttrs> {
  static get attrsSchema(): AttrsSchema<QueryBuilderAttrs> {
    return {
      ...super.attrsSchema,
      'add-entry-label': {
        type: `string`,
        default: ``,
        description: `Label to use on the main events section`,
      },
      'add-entry-button-label': `string`,
      'add-entry-button-tooltip-text': `string`,
      'add-exclusion-event-label': {type: `string`, default: `Add exclusion step`},
      'allow-cohort-filter': `boolean`,
      'behavior-mode': `boolean`,
      'breakdown-attribution': `json`,
      'breakdown-attribution-selection': `string`,
      'attribution-tooltip-data': `json`,
      'breakdown-resource-types': {
        type: `json`,
        default: [],
        description: `list of ResourceTypes to use in the breakdown menu`,
      },
      'can-customize-numeric-buckets': `boolean`,
      'can-group-events': {
        type: `boolean`,
        description: `Allow adding additional events at the same entry index`,
      },
      'data-group-id': `string`,
      'data-groups-enabled': `boolean`,
      'date-range': `json`,
      'disable-add-event-filters': `boolean`,
      'disable-add-entry': `boolean`,
      'disable-add-grouped-event-reason': `string`,
      'disable-breakdown': `boolean`,
      'disable-clone-entry': `boolean`,
      'disable-delete-entry': `boolean`,
      'disable-first-time-event-filters': `boolean`,
      'enable-enhanced-list-filters': `boolean`,
      'enable-filter-attribution': `boolean`,
      'report-header-attrs': `json`,
      entries: {
        type: `json`,
        default: [],
        description: `interface: QueryMetricEntry[]`,
      },
      'entries-draggable': {
        type: `boolean`,
        description: `enable drag-and-drop reordering on main entry selection`,
      },
      'entry-index-toggleable': {
        type: `boolean`,
        description: `if the row index label can be toggled`,
      },
      'entry-index-toggle-label': `string`,
      'entry-row-label-type': {type: `string`, default: `numeric`},
      'exclude-autotrack-events': {
        type: `boolean`,
        description: `whether to hide autotrack events from the events menu`,
      },
      'exclude-custom-events': {
        type: `boolean`,
        description: `whether to hide custom events from the events menu`,
      },
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
      'exclusion-steps': {
        type: `json`,
        default: [],
        description: `interface: QueryMetricEntry[]`,
      },
      'exclusion-step-add-entry-tooltip-text': `string`,
      'filter-hide-users': `boolean`,
      'filter-hide-data-groups': `boolean`,
      filters: {
        type: `json`,
        default: [],
        description: `interface: EventFilters`,
      },
      'filters-operator': {
        type: `string`,
        enum: [FiltersOperator.And, FiltersOperator.Or],
        default: FiltersOperator.And,
      },
      'group-by': {
        type: `json`,
        default: [],
        description: `interface: Segment`,
      },
      'group-by-undraggable-rows': {
        type: `json`,
        default: [],
        description: `Array of indices for group by rows that cannot be dragged`,
      },
      'header-title': {
        type: `string`,
        description: `Section title for the main event selection`,
      },
      'header-title-lowercase': {
        type: `boolean`,
        description: `Section title in lower case`,
      },
      'inline-cohorts-override': {
        type: `json`,
        description: `overrides the cohort permissions and allow inline cohort creation/editing`,
      },
      'readonly-entries': {
        type: `boolean`,
        description: `renders query builder entry section in read only mode`,
      },
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      'metrics-mode': {
        type: `boolean`,
        description: `enables numeric property selection and aggregation functions`,
      },
      'group-by-limitation': {
        type: `number`,
        default: null,
      },
      'main-entry-non-collapsible': `boolean`,
      'other-group-by-options': {type: `string`, default: `[]`},
      'other-filter-options': {type: `string`, default: `[]`},
      'project-has-people': `boolean`,
      'project-has-cohorts': `boolean`,
      'project-id': `number`,
      'query-type-options': {type: `json`, description: `ToggleOption[]`},
      'row-limit': `number`,
      'row-types': QueryEntrySection.attrsSchema[`row-types`],
      'selected-query-type': `string`,
      'show-entry-rename': `boolean`,
      'show-exclusion-steps': `boolean`,
      'hide-edit-controls': `boolean`,
      'hide-date-picker': `boolean`,
      'hide-data-groups-selector': `boolean`,
      'hide-filter': `boolean`,
      'hide-group-by': `boolean`,
      'show-all-events': `boolean`,
      'show-any-event': `boolean`,
      'show-sessions': `boolean`,
      'show-special-date-property': `boolean`,
      'show-special-dataset-property': `boolean`,
      'support-b2b-cohorts': {
        type: `boolean`,
        description: `whether b2b cohorts are enabled`,
      },
      'time-display-context': {type: `string`, default: DisplayContext.FullLength},
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-query-builder] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
      },
      'track-properties': {
        type: `json`,
        default: {},
        description: `If specified, adds extra properties to the track event`,
      },
      'user-id': `number`,
      'workspace-id': `number`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'sticky-report-header': `boolean`,
      'report-header-only': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        customEvents: [],
        dataGroups: [DEFAULT_DATA_GROUP],
        selectionObject: null,
      },

      helpers: {
        serializeAttributes,
        // TODO: sticky-header @admin
        handleReportHeaderInsert: ({elm}) => {
          const wireStickyHeader = () => {
            // call stickyFixed, and wire-up clean up to run on disconnect
            // ideally we don't want to pass in the height directly here but i'm sure there was a good reason why we did here
            this.onDisconnected(
              stickyFixed(
                elm,
                isBoardsUiEnabled(currentProjectStore.value) ? chromeHeaderPrimaryHeight : chromeHeaderHeight,
              ),
            );
          };

          if (this.helpers.useStickyHeader()) {
            wireStickyHeader();
          } else {
            waitForStoreValue(currentProjectStore, {timeout: 5_000}).then(
              () => {
                if (this.helpers.useStickyHeader()) {
                  wireStickyHeader();
                }
              },
              () => {
                /* Swallow any timeout */
              },
            );
          }
        },
        useStickyHeader: (): boolean =>
          (this.attr(`sticky-report-header`) || currentProjectStore.value?.organizationId === 1) &&
          !this.attr(`report-header-only`),
        isBoardsUiEnabled: (): boolean =>
          isBoardsUiEnabled(currentProjectStore.value) &&
          [`insights`, `funnels`, `retention`, `flows`].includes(this.attr(`report-header-attrs`)[`display-context`]),
        getHeaderActionItemsSlots: () => this.getHeaderActionItemsSlots(),
        getPropertyMenuAttributes: (type) => this.getPropertyMenuAttributes(type),
        getFilterEntryRowType: () => SelectorTypeDefinitions.Filter,
        getSegmentEntryRowType: () => SelectorTypeDefinitions.Segment,
        getNoneRowLabelType: () => RowLabelType.None,
        getDataGroupSelectWarnings: () => this.getDataGroupSelectWarnings(),
        hasSlot: (name) => this.hasSlot(name),
        hasEventsOnly: () => this.hasEventsOnly,
        hasUserEntriesOnly: () => this.hasUserEntriesOnly,
        onDataGroupSelected: ({detail: {dataGroup}}: CustomEvent) => this.emitUpdateEvent({dataGroupId: dataGroup.id}),
        onSavedCustomEvent: () => this.emitUpdateEvent({}, {forceQuery: true}),
        onQueryTypeSelected: (ev) => this.emitUpdateEvent({queryType: ev.detail.selected}),
        preventTracking: (ev) => ev.stopPropagation(),
        propagateEvent: (ev) => propagateEvent(this, ev),
        updateGroupBy: ({detail}) =>
          this.emitUpdateEvent({groupBy: detail.items || detail.item}, {queryReason: detail.queryReason}),

        selectorTheme: () => qbStyleAttrs().selectorTheme,
        selectorType: () => qbStyleAttrs().selectorType,

        updateAttributionSelection: ({detail}: CustomEvent) => {
          const props = {
            selected_breakdown_attribution_value: detail.item.value,
            selected_breakdown_attribution_type: detail.item.index,
          };
          this.track(TrackingEvent.SelectBreakdownAttribution, props);
          this.emitUpdateEvent({attributionSelection: detail.item.value});
        },
        /** Captures a track event to be tracked as part of this component */
        captureTracking: (ev) => {
          const {eventPrefix, props} = ev.detail;
          const eventName = ev.detail.eventName.replace(eventPrefix, ``);

          if (this.isCapturedTrackEvent(eventName)) {
            this.helpers.preventTracking(ev);
            this.track(eventName, props);
          }
        },

        /** Clears a given attribute by emitting a select event with its default value */
        clearSelection: (attr) => {
          const fieldName = findKeyByValue(AttributeFields, attr);
          const defaultValue = this.getDefaultAttribute(attr);

          this.emitUpdateEvent({[fieldName]: defaultValue});
          this.track(`Clear ${ATTRIBUTE_READABLE_MAP[attr]}`);
        },

        getEntries: (source): QueryEntry[] => this.getEntries(source),

        getEntryValues: (source): string[] => {
          return this.getEntryValues(source);
        },

        getNumExclusionSteps: () => this.getEntries(`entries`).length,

        /** Attribution related helpers */
        showAttributionSelector: () => {
          let showAttributionSelector = false;

          // This is funnels specific logic to determine attribution selection.
          // Oddly, enable-filter-attribution is only used by funnels.
          if (this.attr(`enable-filter-attribution`)) {
            const {groupBy, filters} = this.state.selectionObject;
            let segments: Segment[] = [];
            if (isMultiGroupBy(groupBy)) {
              segments = groupBy.map((segmentsList) => segmentsList[0]);
            } else {
              segments = groupBy;
            }
            const hasEventBreakdown = segments.some((s) => isEventSegment(s));
            const hasEventFilter = filters.some((f) => f.resourceType === ResourceType.Event);
            showAttributionSelector = hasEventBreakdown || hasEventFilter;
          } else {
            showAttributionSelector =
              this.attr(`breakdown-attribution`)?.length > 1 && this.attr(`group-by`)?.length > 0;
          }
          return showAttributionSelector;
        },

        getAttributionSelectedOption: () => {
          const items = this.attr(`breakdown-attribution`);
          for (const item of items) {
            if (item.value === this.attr(`breakdown-attribution-selection`)) {
              return item.label;
            }
          }
        },

        getAttributionContextPanel: (x) => {
          return this.attr(`attribution-tooltip-data`) && this.attr(`attribution-tooltip-data`)[x.value];
        },

        showFilterOrOperatorInfo: () => {
          return this.attr(`filters-operator`) === FiltersOperator.Or;
        },
        /** Attributes for mp-query-entry-section metric analysis */
        getMetricsModeAttributes: () => {
          if (!this.attr(`metrics-mode`)) {
            return {};
          }

          return serializeAttributes({
            'row-label-type': `alpha`,
            'show-all-events': true,
            'show-top-events': true,
          });
        },

        /**
         * Determines if a current attribute differs from its default value
         */
        hasValue: (attr): boolean => !isEqual(this.getDefaultAttribute(attr), this.attr(attr)),

        updateEntrySection: ({detail: {entries, queryReason}}: EntrySectionUpdateEvent, source) => {
          this.updateEntries(entries as QueryEntry[], source, {queryReason});
        },

        getFilterEntries: (): QueryFilterEntry[] => {
          return this.attr(`filters`).map((filterItem) => ({
            event: filterItem,
            // global filters do not render and/or
            filtersOperator: null,
            filters: [],
            type: SelectorTypeDefinitions.Filter,
          }));
        },

        getSegmentEntries: (): QuerySegmentEntry[] => {
          return transformToQuerySegmentEntries(this.attr(`group-by`));
        },

        updateFilterEntrySection: ({detail: {entries, queryReason}}: EntrySectionUpdateEvent) => {
          const filters: Filter[] = (entries as QueryFilterEntry[]).map((entry) => entry.event);
          this.emitUpdateEvent({filters}, {queryReason});
        },

        updateSegmentEntrySection: ({detail: {entries, queryReason}}: EntrySectionUpdateEvent) => {
          const groupBy = transformFromQuerySegmentEntries(entries as QuerySegmentEntry[]);
          this.emitUpdateEvent({groupBy}, {queryReason});
        },

        actionClick: (ev) => {
          const action = ev.detail.value;

          if (action === `refresh`) {
            this._refreshEventsAndProperties();
          }
          propagateEvent(this, ev);
        },
      },
    };
  }

  get hasEventsOnly() {
    // TODO some empty states from transforms don't pass in type, fix to remove the !entry.type check
    return this.state.selectionObject[`entries`].every(
      (entry) => !entry || isEventBasedType(entry.type) || isGroupedEntry(entry),
    );
  }

  get hasUserEntriesOnly() {
    return this.state.selectionObject[`entries`].every((entry) => isUserBasedType(entry));
  }

  private dispatchUpdate: (ev: CustomEvent) => void;
  private pendingQueryReason: QueryReason;

  constructor() {
    super();
    // we use debounce such that even if we have multiple updates within a frame, we send the last
    // one up, to not spam network queries.
    this.dispatchUpdate = debounce((ev) => {
      ev.detail.queryReason = this.pendingQueryReason;
      delete this.pendingQueryReason;
      this._dispatchUpdate(ev);
    }, 100);
    this.update({
      selectionObject: this.getSelectionObjectFromAttrs(),
    });
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (Object.values(AttributeFields).includes(attr)) {
      /**
       * a single object representing the current
       * selection state of all passed attributes
       */

      // Whenever we get new data attributes, we buffer them to this.selectionObject. This is
      // because within a frame, we might have multiple update events, from multiple components
      // handling callbacks underneath. Since the frame hasn't finished, the attributes are not
      // updated yet, so reading from them would give us a outdated view of the world. Instead, we
      // use selectionObject to buffer any changes to the state within a frame, and reset it when
      // we come to the next frame (when attributes are changed)
      this.update({selectionObject: this.getSelectionObjectFromAttrs()});
    } else if (attr === `project-id` && this.initialized) {
      this.fetchProjectData();
    } else if (attr === `workspace-id` && this.initialized) {
      this.fetchProjectData();
    }
  }

  public connectedCallback() {
    super.connectedCallback();

    this.update({
      selectionObject: this.getSelectionObjectFromAttrs(),
    });

    this.fetchProjectData();
  }

  private getHeaderActionItemsSlots() {
    const controlItems = this.attr(`report-header-attrs`)[`control-items`];
    return controlItems.filter((item) => Object.prototype.hasOwnProperty.call(item, `slot`));
  }

  private getSelectionObjectFromAttrs(): QueryBuilderSelection {
    return mapValues(AttributeFields, (attribute) => this.attr(attribute));
  }

  /**
   * Checks for presence of a slotted child element
   */
  private hasSlot(slotName: string): boolean {
    return !!this.querySelector(`[slot="${slotName}"]`);
  }

  private getDataGroupSelectWarnings(): SelectWarning[] {
    return this.state.dataGroups.map((dataGroup) => {
      const dataGroupId = dataGroup.groupID;
      const showWarning = queryIsInvalidForDataGroup({
        dataGroupId,
        query: this.state.selectionObject,
        customEvents: this.state.customEvents,
      });
      return {dataGroupId, showWarning};
    });
  }

  /**
   * Returns the default value from attrsSchema for a
   * given attribute, throws an error where no default
   * value is explicitly defined
   */
  private getDefaultAttribute(attrName: string) {
    const attribute = QueryBuilder.attrsSchema[attrName];

    if (typeof attribute === `object` && Object.prototype.hasOwnProperty.call(attribute, `default`)) {
      return attribute.default;
    }

    throw new Error(`No default value found for ${attrName}`);
  }

  // TODO(pablo): this method seems to add unncessary indirection, these values
  // can be computed directly in the template
  private getPropertyMenuAttributes(type: string) {
    const groupsEnabled = this.attr(`data-groups-enabled`);
    const dataGroupsEnabledAttr = `data-groups-enabled`;

    const attrs = {
      'include-custom-properties': this.attr(`include-custom-properties`),
      'track-name': this.attr(`track-name`),
    };

    let otherAttrs;
    if (this.hasEventsOnly) {
      const types = this.attr(`breakdown-resource-types`);
      if (types.length && type === `breakdown`) {
        otherAttrs = {
          [dataGroupsEnabledAttr]: false,
          'project-has-cohorts': types.includes(ResourceType.Cohort),
          'project-has-people': types.includes(ResourceType.User),
          'hide-events': !types.includes(ResourceType.Event),
        };
      } else {
        otherAttrs = {
          [dataGroupsEnabledAttr]: groupsEnabled,
          'project-has-cohorts': this.attr(`project-has-cohorts`),
          'project-has-people': this.attr(`project-has-people`),
          'hide-events': false,
        };
      }
    } else {
      otherAttrs = {
        [dataGroupsEnabledAttr]: groupsEnabled && (type === `breakdown` ? this.hasUserEntriesOnly : true),
        'project-has-cohorts': this.attr(`project-has-cohorts`),
        // when combining cohorts & events, only user filtering is possible
        'project-has-people':
          (type === `breakdown` ? this.hasUserEntriesOnly : true) && this.attr(`project-has-people`),
        'hide-events': true,
      };
    }

    return {...attrs, ...otherAttrs};
  }

  private getEntries(source: EventSourceName): QueryEntry[] {
    // @ts-ignore types of `type` are incompatible
    return this.attr(source).map((entry) => ({
      ...entry,
      type: entry.type || ResourceType.Event,
    }));
  }

  private getEntryValues(source: EventSourceName): string[] {
    return this.getEntries(source).flatMap((entry) => {
      if (isGroupedEntry(entry)) {
        return entry.event.map((event) => event.event.value);
      } else if (isEventBasedType(entry.type)) {
        return entry.event.value;
      }
    });
  }

  private updateEntries(
    entries: QueryEntry[],
    source: EventSourceName,
    {queryReason}: {queryReason?: QueryReason} = {},
  ) {
    this.emitUpdateEvent({[EVENT_SOURCE_MAP[source]]: entries}, {queryReason});
  }

  private emitUpdateEvent(
    updates: Partial<QueryBuilderSelection>,
    {
      forceQuery = false,
      queryReason = QueryReason.QbOtherUpdate,
    }: {forceQuery?: boolean; queryReason?: QueryReason} = {},
  ) {
    const existingSelection = this.state.selectionObject;
    let selection = {...existingSelection, ...updates};
    selection = {
      ...selection,
      exclusionSteps: sanitizeExclusionSteps(selection.exclusionSteps, selection.entries),
      groupBy: sanitizeGroupBys(selection.entries, selection.groupBy),
    };
    if (
      Object.prototype.hasOwnProperty.call(updates, `dataGroupId`) &&
      !dataGroupIDsEqual(updates.dataGroupId, existingSelection.dataGroupId)
    ) {
      selection = setQueryDataGroup({
        query: selection,
        dataGroupId: updates.dataGroupId,
        customEvents: this.state.customEvents,
      });
    }
    const detail = {
      ...selection,
      forceQuery,
      delta: updates,
    };
    this.update({selectionObject: selection});

    this.pendingQueryReason = maxQueryReason([this.pendingQueryReason, queryReason]);
    this.dispatchUpdate(new CustomEvent(`update`, {detail}));
  }

  private _dispatchUpdate(ev) {
    this.dispatchEvent(ev);
  }

  /**
   * Emits track events that bubble up
   */
  private track(event: string, props: UnknownObj = {}) {
    if (!this.attr(`track-name`)) {
      return;
    }

    if (this.isCapturedTrackEvent(event)) {
      emitTrackEvent(this, {
        eventName: event,
        eventPrefix: COMPONENT_NAME,
        props: {
          project_id: this.attr(`project-id`),
          tab: this.attr(`track-name`),
          source: `query-builder`,
          ...(this.attr(`query-type-options`) ? {'query type': this.attr(`selected-query-type`)} : {}),
          // props from propagated track is last so that they can override any props added by this
          // component
          ...props,
        },
        cancelable: true,
      });
    } else {
      throw new Error(`tracking event not defined: ${event}`);
    }
  }

  private isCapturedTrackEvent(event: string) {
    return findKey(TrackingEvent, (eventName) => eventName === event) || CAPTURED_TRACKING.has(event);
  }

  private fetchDataGroups({projectId, workspaceId}) {
    return dataGroupsClient.query({projectId, workspaceId});
  }

  private fetchCustomEvents({projectId, workspaceId}) {
    return customEventsClient.query({projectId, workspaceId});
  }

  private fetchProjectData() {
    const projectId = this.attr(`project-id`);
    const workspaceId = this.attr(`workspace-id`);
    if (!projectId && !workspaceId) {
      // This check is necessary because retention adds mp-query-builder to the dom before setting attributes, so
      // they are blank when this method is called in connectedCallback
      return;
    }
    const paramsUnchanged = () => projectId === this.attr(`project-id`) && workspaceId === this.attr(`workspace-id`);

    this.fetchCustomEvents({projectId, workspaceId}).then((customEvents) => {
      if (paramsUnchanged()) {
        this.update({customEvents});
      }
    });
    if (this.attr(`data-groups-enabled`)) {
      this.fetchDataGroups({projectId, workspaceId}).then((dataGroups) => {
        if (paramsUnchanged()) {
          this.update({dataGroups: [DEFAULT_DATA_GROUP, ...dataGroups]});
        }
      });
    }
  }

  _refreshEventsAndProperties() {
    const projectId = this.attr(`project-id`);
    const workspaceId = this.attr(`workspace-id`);

    topEventsStore.refresh({projectId, workspaceId});
    eventDefinitionsStore.refresh({projectId, workspaceId});

    peoplePropertiesStore.refresh({projectId, workspaceId, includeDimensionProperties: true});
    peoplePropertyDefinitionsStore.refresh({category: `events`, projectId, workspaceId});

    const initialParams = {
      projectId,
      ...(workspaceId ? {workspaceId} : {workspaceId: 0}),
      includeHidden: true,
      includeDimensionProperties: true,
    };
    eventPropertiesStore.refresh(initialParams);
    eventPropertyDefinitionsStore.refresh({category: `user`, projectId, workspaceId});

    // Update event properties for currently selected events
    const eventNames = this.getEntryValues(`entries`).filter(
      (eventName) => !(eventName in [SPECIAL_EVENTS.ALL_EVENTS, SPECIAL_EVENTS.ANY_EVENT, SPECIAL_EVENTS.TOP_EVENTS]),
    );
    for (const eventName of eventNames) {
      const params = {
        ...initialParams,
        eventName: [eventName],
      };
      const storeKey = Store.getKey(params);
      eventPropertiesStore.refresh(params, {}, storeKey);
    }
  }
}

export const QueryBuilderEl = defineAndMakeJsxFactory(COMPONENT_NAME, QueryBuilder);
